<template>
  <div>
    <label v-html="dateLabel"></label>
    <v-row no-gutters>
      <v-col
        v-for="(segment, index) in segments"
        :key="index"
        :cols="segment.isLarge ? 12 : 6"
        :sm="segment.isLarge ? 6 : 3"
        :class="segment.class"
      >
        <v-text-field
          v-bind="commonProps"
          :id="`date-${segment.identifier}`"
          v-model="$v[segment.identifier].$model"
          :data-cy="`dateSegmentsDate_${segment.identifier}`"
          :autofocus="autofocus && index === 0"
          :placeholder="placeholders[segment.identifier]"
          :data-part="segment.identifier"
          :class="{
            'is-valid':
              $v.$anyDirty &&
              !$v[segment.identifier].$invalid &&
              $v[segment.identifier].$model.length &&
              !dateError.length
          }"
          :error="!!dateError.length"
          :error-messages="dateError"
          :pattern="segment.pattern"
          v-on="commonEvents"
        />
      </v-col>
    </v-row>
    <div
      v-if="(!!dateHelpText && !!dateHelpText.length) || !!dateError.length"
      class="v-text-field__details"
    >
      <div
        :class="{ 'error--text': !!dateError.length }"
        class="v-messages theme--light"
        role="alert"
      >
        <div class="v-messages__wrapper">
          <div
            :inner-html.prop="
              !!dateError.length ? dateError : dateHelpText.length ? dateHelpText : ''
            "
            class="v-messages__message text-body-small"
          />
        </div>
      </div>
    </div>
    <v-checkbox
      v-if="todayDateCheckbox"
      v-model="todayDateUsed"
      data-cy="dateSegmentsToday"
      :label="todayDateCheckboxLabel"
      class="mt-4"
      hide-details
      @change="onTodayDateCheckboxChange"
      @click="$emit('todayClick', $event)"
    />
  </div>
</template>

<script>
import {
  between,
  numeric,
  required,
  requiredIf,
  minValue,
  maxValue,
  helpers
} from 'vuelidate/lib/validators';

export default {
  name: 'DateSegments',
  props: {
    identifier: {
      type: [String, Number],
      required: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      required: true
    },
    dateHelpText: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      required: false
    },
    errorTextDateRange: {
      type: String,
      required: false
    },
    errorTextAge: {
      type: String,
      required: false
    },
    dateLabel: {
      type: String,
      default: ''
    },
    maxValue: {
      type: String,
      default: null
    },
    minValue: {
      type: String,
      default: null
    },
    required: {
      type: Boolean,
      default: false
    },
    todayDateCheckbox: {
      type: Boolean,
      default: false
    },
    todayDateCheckboxLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      commonProps: {
        disabled: this.disabled,
        flat: true,
        hideDetails: true,
        solo: true,
        autocomplete: 'off',
        type: 'number',
        hideSpinButtons: true,
        validateListener: null,
        resetListener: null
      },
      commonEvents: {
        keyup: this.onKeyUp,
        blur: this.onBlur,
        focus: this.onFocus
      },
      date: '',
      month: '',
      year: '',
      dateObject: null,
      placeholders: {
        date: '',
        month: '',
        year: ''
      },
      firstEmptyBackspace: false,
      todayDateUsed: false
    };
  },
  computed: {
    dateError() {
      if (!this.$v.$anyDirty) {
        return '';
      }

      const requiredProp = this.required ? 'required' : 'requiredIf';

      if (
        this.$v.year[requiredProp] === false ||
        this.$v.month[requiredProp] === false ||
        this.$v.date[requiredProp] === false
      ) {
        return this.errorText || this.$t('preventionForm.birthDate.errorText');
      }

      if (!this.$v.month.between && !!this.$v.month.$params.between) {
        return this.$t('dateInput.errors.invalidFormatMonth', {
          min: this.$v.month.$params.between.min,
          max: this.$v.month.$params.between.max
        });
      }

      if (!this.$v.date.between && !!this.$v.date.$params.between) {
        return this.$t('dateInput.errors.invalidFormatDate', {
          min: this.$v.date.$params.between.min,
          max: this.$v.date.$params.between.max
        });
      }
      if (this.$v.dateObject.isAdult === false) {
        return this.errorTextAge || this.$t('dateInput.errors.labTests.errorUnder18');
      }
      if (this.$v.$anyError) {
        return (
          this.errorTextDateRange || this.$t('preventionForm.birthDate.errorTextBirthDateRange')
        );
      }
      return '';
    },
    segments() {
      return [
        {
          identifier: 'date',
          isLarge: false,
          pattern: '[0-9]{1,2}'
        },
        {
          identifier: 'month',
          isLarge: false,
          class: 'pl-2',
          pattern: '[0-9]{1,2}'
        },
        {
          identifier: 'year',
          isLarge: true,
          class: 'pt-2 pt-sm-0 pl-sm-2',
          pattern: '[0-9]{4}'
        }
      ];
    }
  },
  watch: {
    value(newValue) {
      if (!newValue) {
        this.year = '';
        this.month = '';
        this.date = '';
        this.dateObject = null;
        return;
      }

      const monthCst = /^(\d{4})-(\d{2})-(\d{2})/.exec(newValue);
      if (!monthCst) {
        return;
      }

      const nextYear = monthCst[1];
      const nextMonth = monthCst[2];
      const nextDate = monthCst[3];

      const hasChanged =
        nextYear !== this.year || nextMonth !== this.month || nextDate !== this.date;

      if (hasChanged) {
        this.year = nextYear;
        this.month = nextMonth;
        this.date = nextDate;
        const dateObj = new Date(Number(nextYear), Number(nextMonth) - 1, Number(nextDate));
        dateObj.setHours(0, 0, 0, 0);
        this.dateObject = dateObj;
      }
    }
  },
  validations() {
    const minDate = this.minValue ? new Date(this.minValue) : null;
    const maxDate = this.maxValue ? new Date(this.maxValue) : null;
    const needAgeValidation =
      this.$route.query.dfn === 'interpretation-of-results' ||
      this.$route.fullPath.includes('ulhc');

    let dateMaxValue = 31;
    let dateMinValue = 1;
    if (this.month && this.year) {
      dateMaxValue = new Date(this.year, this.month, 0).getDate();
      if (maxDate?.getFullYear() === this.year && maxDate?.getMonth() + 1 === this.month) {
        dateMaxValue = maxDate.getDate();
      }
      if (minDate?.getFullYear() === this.year && minDate?.getMonth() + 1 === this.month) {
        dateMinValue = minDate.getDate();
      }
    }

    let monthMaxValue = 12;
    let monthMinValue = 1;
    if (this.year) {
      if (maxDate?.getFullYear() === this.year) {
        monthMaxValue = maxDate.getMonth() + 1;
      }
      if (minDate?.getFullYear() === this.year) {
        monthMinValue = minDate.getMonth() + 1;
      }
    }

    const yearRules = {
      maxValue: maxDate ? maxValue(maxDate.getFullYear()) : {},
      minValue: minDate ? minValue(minDate.getFullYear()) : {},
      numeric
    };

    const monthRules = {
      between: between(monthMinValue, monthMaxValue),
      numeric
    };

    const dateRules = {
      between: between(dateMinValue, dateMaxValue),
      numeric
    };

    if (this.required) {
      yearRules.required = required;
      monthRules.required = required;
      dateRules.required = required;
    } else {
      yearRules.requiredIf = requiredIf(function () {
        return !!this.date || !!this.month;
      });
      monthRules.requiredIf = requiredIf(function () {
        return !!this.date || !!this.year;
      });
      dateRules.requiredIf = requiredIf(function () {
        return !!this.year || !!this.month;
      });
    }

    const dateObjectRules = {};
    if (minDate) {
      dateObjectRules.minValue = minValue(minDate);
    }
    if (maxDate) {
      dateObjectRules.maxValue = maxValue(maxDate);
    }
    if (needAgeValidation) {
      const isAdult = helpers.withParams({ type: 'isAdult' }, () => {
        if (!this.dateObject || isNaN(this.dateObject.getTime())) {
          return true;
        }
        const today = new Date();
        const testDate = this.dateObject;
        let age = today.getFullYear() - testDate.getFullYear();
        const month = today.getMonth() - testDate.getMonth();
        if (month < 0 || (month === 0 && today.getDate() < testDate.getDate())) {
          age--;
        }
        return age >= 18;
      });
      dateObjectRules.isAdult = isAdult;
    }

    return {
      year: yearRules,
      month: monthRules,
      date: dateRules,
      dateObject: dateObjectRules
    };
  },
  created() {
    this.validateListener = done => {
      this.$v.$touch();
      this.$nextTick(() => {
        done(!this.$v.$invalid);
      });
    };

    this.$eventHub.$on(
      this.identifier ? `validateDateSegments_${this.identifier}` : 'validateDateSegments',
      this.validateListener
    );

    this.resetListener = () => {
      this.$v.$reset();
    };

    this.$eventHub.$on(
      this.identifier
        ? `resetDateSegmentsValidation_${this.identifier}`
        : 'resetDateSegmentsValidation',
      this.resetListener
    );

    this.placeholders.year = this.$t('placeholders.date.year');
    this.placeholders.month = this.$t('placeholders.date.month');
    this.placeholders.date = this.$t('placeholders.date.date');

    if (this.value) {
      const initialValue = new Date(this.value);

      if (!isNaN(initialValue.valueOf())) {
        this.year = initialValue.getFullYear();
        this.month = this.padStartWithZeroes(initialValue.getMonth() + 1, 2);
        this.date = this.padStartWithZeroes(initialValue.getDate(), 2);
        this.dateObject = initialValue;
      }
    }
  },
  beforeDestroy() {
    this.$eventHub.$off(
      this.identifier ? `validateDateSegments_${this.identifier}` : 'validateDateSegments',
      this.validateListener
    );
    this.$eventHub.$off(
      this.identifier
        ? `resetDateSegmentsValidation_${this.identifier}`
        : 'resetDateSegmentsValidation',
      this.resetListener
    );
  },
  methods: {
    preventScroll(evt) {
      evt.preventDefault();
    },
    onKeyUp(evt) {
      if (evt.keyCode === this.keyCodes.shift) {
        return;
      }

      evt.preventDefault();
      this.todayDateUsed = false;
      this.$v.$touch();

      const part = evt.target.dataset.part;
      let prevPart = null;
      let nextPart = null;
      switch (part) {
        case 'date':
          nextPart = 'month';
          break;
        case 'month':
          prevPart = 'date';
          nextPart = 'year';
          break;
        case 'year':
          prevPart = 'month';
          break;
      }

      switch (evt.keyCode) {
        case this.keyCodes.backspace:
          // eslint-disable-next-line no-case-declarations
          const isEmpty =
            `${this[part]}`.length === 0 && this.$el.querySelector(`#date-${prevPart}`);
          if (isEmpty && !this.firstEmptyBackspace) {
            this.firstEmptyBackspace = true;
            return;
          } else if (isEmpty && this.firstEmptyBackspace) {
            const prevInput = this.$el.querySelector(`#date-${prevPart}`);
            if (prevInput) {
              document.documentElement.classList.add('no-scroll');
              prevInput.focus();
              prevInput.select();
              document.documentElement.classList.remove('no-scroll');
            }
          }
          break;
        case this.keyCodes.up:
        case this.keyCodes.down:
          break;
        default:
          if (evt.keyCode !== 229 && isNaN(parseInt(evt.key))) {
            return;
          } else {
            const maxLength = part === 'year' ? 4 : 2;
            const currentValueLength = `${this[part]}`.length;

            if (currentValueLength >= maxLength) {
              if (this.$el.querySelector(`#date-${nextPart}`)) {
                const nextInput = this.$el.querySelector(`#date-${nextPart}`);
                if (nextInput) {
                  setTimeout(() => {
                    nextInput.scrollIntoView({ block: 'nearest' });
                    nextInput.focus();
                    nextInput.select();
                  }, 50);
                }
              } else if (part === 'year' && currentValueLength > 3) {
                const yearInput = this.$el.querySelector('#date-year');
                if (yearInput) {
                  yearInput.blur();
                  this.$emit('blur');
                }
              }
            }
          }
          break;
      }
      this.firstEmptyBackspace = false;

      this.callInput();
    },
    onBlur(evt) {
      const partBlurred = evt.target.dataset.part;

      if (partBlurred === 'year') {
        if (this[partBlurred].length > 4) {
          this[partBlurred] = this[partBlurred].slice(0, 4);
        }
        this.$emit('blurPart', partBlurred);
        evt.target.removeEventListener('wheel', this.preventScroll);
        return;
      }
      if (!this[partBlurred]) {
        return;
      }
      this[partBlurred] = this.padStartWithZeroes(this[partBlurred], 2);
      this.$emit('blurPart', partBlurred);
      evt.target.removeEventListener('wheel', this.preventScroll);
    },
    onFocus(evt) {
      evt?.target?.addEventListener('wheel', this.preventScroll);
      this.$emit('focus', evt);
    },
    onTodayDateCheckboxChange(checked) {
      if (checked) {
        const today = new Date();
        this.year = `${today.getFullYear()}`;
        this.month = `${today.getMonth() + 1}`;
        this.date = `${today.getDate()}`;

        this.$v.$touch();
        if (!this.$v.$invalid) {
          this.callInput();
        }
      }
    },
    callInput() {
      const year = `${this.year ?? ''}`;
      const monthRaw = `${this.month ?? ''}`;
      const dateRaw = `${this.date ?? ''}`;

      const allEmpty = !year && !monthRaw && !dateRaw;
      if (allEmpty) {
        this.dateObject = null;
        this.$emit('input', '');
        return;
      }

      const yearComplete = year.length === 4 && !isNaN(Number(year));
      const monthComplete = monthRaw.length >= 1 && !isNaN(Number(monthRaw));
      const dateComplete = dateRaw.length >= 1 && !isNaN(Number(dateRaw));

      const month = monthComplete ? this.padStartWithZeroes(monthRaw, 2) : '';
      const date = dateComplete ? this.padStartWithZeroes(dateRaw, 2) : '';

      const fullComplete = yearComplete && month.length === 2 && date.length === 2;
      if (!fullComplete) {
        return;
      }

      const iso = `${year}-${month}-${date}`;
      const dateOb = new Date(Number(year), Number(month) - 1, Number(date));
      dateOb.setHours(0, 0, 0, 0);
      this.dateObject = dateOb;

      this.$emit('input', iso);
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep {
  input {
    text-align: center;
  }
}
</style>
